import { categoryLabel } from '../types/allergens.js';
import { Confidence } from '../types/detection.js';
export function generateDeclaration(tokens, detections, mode) {
    const relevantDetections = detections.filter((d) => d.confidence !== Confidence.NotDetected);
    const uniqueAllergens = [
        ...new Set(relevantDetections
            .filter((d) => d.allergenCategory !== 'unknown')
            .map((d) => categoryLabel(d.allergenCategory))),
    ];
    const reviewItems = relevantDetections.filter((d) => d.confidence === Confidence.NeedsReview);
    const presentDetections = relevantDetections.filter((d) => d.confidence === Confidence.Present || d.confidence === Confidence.Possible);
    const usStatement = mode === 'US' ? generateUSStatement(presentDetections, reviewItems) : '';
    const euList = mode === 'EU' ? generateEUList(tokens, relevantDetections) : '';
    const euHtml = mode === 'EU' ? generateEUHtml(tokens, relevantDetections) : '';
    const output = {
        mode,
        allergens: relevantDetections,
        uniqueAllergens,
        usText: usStatement,
        euText: euList,
        euHtml,
        hasAmbiguous: reviewItems.length > 0,
        reviewCount: reviewItems.length,
    };
    return output;
}
function generateUSStatement(present, review) {
    if (present.length === 0 && review.length === 0) {
        return 'Contains: [None detected]';
    }
    if (present.length === 0 && review.length > 0) {
        return 'Contains: [Review required — ambiguous items found]';
    }
    // Group by category
    const grouped = new Map();
    for (const det of present) {
        if (det.allergenCategory === 'unknown')
            continue;
        const existing = grouped.get(det.allergenCategory);
        if (existing) {
            if (det.species)
                existing.add(det.species);
        }
        else {
            grouped.set(det.allergenCategory, new Set(det.species ? [det.species] : []));
        }
    }
    const parts = [];
    for (const [category, species] of grouped) {
        const name = category === 'cereals_containing_gluten' ? 'Wheat' : categoryLabel(category);
        if (species.size > 0) {
            const speciesList = [...species].join(', ');
            parts.push(`${name} (${speciesList})`);
        }
        else {
            parts.push(name);
        }
    }
    parts.sort();
    let statement = `Contains: ${parts.join(', ')}`;
    if (review.length > 0) {
        statement += ' — [Additional items require review]';
    }
    return statement;
}
function generateEUList(tokens, detections) {
    if (tokens.length === 0)
        return '';
    // Build a map of ingredient index → detection terms to highlight
    const highlightRanges = new Map();
    for (const detection of detections) {
        if (detection.confidence === Confidence.NotDetected)
            continue;
        // Find the token this detection belongs to
        const tokenIndex = tokens.findIndex((t) => t.id === detection.ingredientId);
        if (tokenIndex === -1)
            continue;
        const token = tokens[tokenIndex];
        const existing = highlightRanges.get(tokenIndex) ?? [];
        // Highlight the entire ingredient name for simplicity
        existing.push({ start: 0, end: token.name.length });
        highlightRanges.set(tokenIndex, existing);
    }
    const parts = tokens.map((token, index) => {
        const ranges = highlightRanges.get(index);
        if (ranges && ranges.length > 0) {
            return `<strong>${escapeHtml(token.rawText)}</strong>`;
        }
        return escapeHtml(token.rawText);
    });
    return parts.join(', ');
}
function generateEUHtml(tokens, detections) {
    if (tokens.length === 0)
        return '';
    const detectedTokenIds = new Set(detections
        .filter((d) => d.confidence !== Confidence.NotDetected)
        .map((d) => d.ingredientId));
    const parts = tokens.map((token) => {
        const escaped = escapeHtml(token.rawText);
        if (detectedTokenIds.has(token.id)) {
            return `<strong>${escaped}</strong>`;
        }
        return escaped;
    });
    return parts.join(', ');
}
function escapeHtml(text) {
    return text
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;');
}
//# sourceMappingURL=generateDeclaration.js.map